import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { SlotState, ChordObjectModelV3, TimeSignature } from '../types';
import { KeySignature, formatBeats, type BarSlotLayout } from '@chordbaker/music-theory';
import ChordEditorModal from './ChordEditorModal';
import LyricsInputModal from './LyricsInputModal';
import SlotArea from './SlotArea';
import SlotActionPill from './SlotActionPill';
import SilenceIcon from './icons/SilenceIcon';
import BeatDots from './BeatDots';
import { beatDotsWidth, buildBeatDots } from '../utils/beatsUtils';
import { getChordDisplayText, getChordNotes } from '@chordbaker/music-theory';
import { getChordDegree } from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { CONSTANTS } from '../constants';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme, showLyrics: boolean = false) => ({
  slotContainer: {
    height: 'auto',
    minHeight: 'auto',
    position: 'relative' as const,
    backgroundColor: theme.colors.slotBackground,
    minWidth: theme.sizes.slot.minWidth,
    overflow: 'hidden' as const,
  },
  // One full-height divider on the slot's right edge (replaces per-area lines):
  // the bar line, so only a bar's last slot draws it
  slotSeparator: {
    position: 'absolute' as const,
    right: 0,
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: theme.colors.slotBorder,
    pointerEvents: 'none' as const,
  },
  // Between slots sharing a bar: short and dashed, so the bar still reads as
  // one. Structure, not decoration: drawn with Beats off too.
  beatDivider: {
    position: 'absolute' as const,
    right: 0,
    top: '28%',
    bottom: '28%',
    width: 0,
    borderRight: `1px dashed ${theme.colors.borderSecondary}`,
    pointerEvents: 'none' as const,
  },
  slotWithChordContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    minHeight: theme.sizes.slot.chordMinHeight,
  },
  emptySlotContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  slotInner: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    flex: 1,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
  },
  lyricsInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    flex: 1,
  },
  degreeText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    color: theme.colors.key,
    marginBottom: theme.spacing.xxs,
    whiteSpace: 'nowrap' as const,
  },
  chordText: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    whiteSpace: 'nowrap' as const,
  },
  notesText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.notes,
    marginTop: theme.spacing.xs,
    whiteSpace: 'nowrap' as const,
    letterSpacing: -0.5,
  },
  lyricsText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.lyrics,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
  },
  lyricsPlaceholder: {
    fontStyle: 'italic' as const,
    color: theme.colors.textSecondary,
  },
  emptySlotText: {
    color: theme.colors.textSecondary,
    fontSize: theme.typography.sizes.sm,
    fontStyle: 'italic' as const,
    textAlign: 'center' as const,
    whiteSpace: 'pre-line' as const,
  },
  silenceMark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  measurementElement: {
    position: 'absolute' as const,
    opacity: 0,
    pointerEvents: 'none' as const,
    left: -9999,
    whiteSpace: 'nowrap' as const,
  },
});

/** Where the slot sits in its bar. A bar's last slot draws the bar line; the others a dashed divider. */
export interface SlotBarPosition {
  isFirstInBar: boolean;
  isLastInBar: boolean;
  /** The slot's place in the bar (`layoutBarSlots`), for the beat dots. */
  layout: BarSlotLayout;
}

export interface SlotProps {
  slot: SlotState;
  onAddSlot: (afterSlotId: string) => void;
  onAddSlotBefore: (beforeSlotId: string) => void;
  onDeleteSlot: (slotIdToDelete: string) => void;
  onUpdateChord: (slotId: string, newChordData: ChordObjectModelV3 | null, customText?: string) => void;
  isLast?: boolean;
  canDelete?: boolean;
  showNotes?: boolean;
  showDegrees?: boolean;
  showLyrics?: boolean;
  selectedKey?: KeySignature | null;
  activeSlotId?: string | null;
  onSlotPress?: (slotId: string) => void;
  onUpdateLyrics?: (slotId: string, lyrics: string) => void;
  /** Defaults to a slot alone in its bar. */
  barPosition?: SlotBarPosition;
  /** The row's time signature; null when the song has none (no dots). */
  meter?: TimeSignature | null;
  /** Beats is on: dots under the chord and the beats button in the pill. */
  showBeats?: boolean;
  onOpenBeatsEditor?: (slotId: string) => void;
  /** + before/after was clicked on an inner slot of a split bar. */
  onCantAddInside?: (slotId: string) => void;
  /** Set when the song has no time signature: the editor's Beats shortcut asks for one instead. */
  onRequestSetTimeSignature?: () => void;
  /** Open the chord editor now (a slot the user just added), then report back. */
  shouldOpenChordEditor?: boolean;
  onChordEditorOpened?: () => void;
}

const Slot: React.FC<SlotProps> = ({
  slot,
  onAddSlot,
  onAddSlotBefore,
  onDeleteSlot,
  onUpdateChord,
  isLast = false,
  canDelete = true,
  showNotes = false,
  showDegrees = false,
  showLyrics = false,
  selectedKey = null,
  activeSlotId,
  onSlotPress,
  onUpdateLyrics,
  barPosition,
  meter = null,
  showBeats = false,
  onOpenBeatsEditor,
  onCantAddInside,
  onRequestSetTimeSignature,
  shouldOpenChordEditor = false,
  onChordEditorOpened,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme, showLyrics), [theme, showLyrics]);
  const [isChordEditorOpen, setIsChordEditorOpen] = useState(false);
  const [isLyricsModalOpen, setIsLyricsModalOpen] = useState(false);

  // A freshly added slot opens its chord editor right away
  useEffect(() => {
    if (shouldOpenChordEditor) {
      setIsChordEditorOpen(true);
      onChordEditorOpened?.();
    }
  }, [shouldOpenChordEditor, onChordEditorOpened]);

  // State for dynamic width calculation
  const [slotWidths, setSlotWidths] = useState({
    chordOnly: theme.sizes.slot.minWidth,
    chordAndNotes: theme.sizes.slot.minWidth,
    chordAndLyrics: theme.sizes.slot.minWidth,
    chordNotesAndLyrics: theme.sizes.slot.minWidth,
  });

  // Refs for measuring text dimensions
  const measureChordTextRef = useRef<HTMLSpanElement>(null);
  const measureNotesTextRef = useRef<HTMLSpanElement>(null);
  const measureLyricsTextRef = useRef<HTMLSpanElement>(null);
  const previousContentRef = useRef<string>('');
  const slotContainerRef = useRef<HTMLDivElement>(null);

  const isFirstInBar = barPosition?.isFirstInBar ?? true;
  const isLastInBar = barPosition?.isLastInBar ?? true;

  // + before/after adds a whole bar, so only a bar's edges take one
  const handleAddSlot = () => {
    if (isLastInBar) onAddSlot(slot.id);
    else onCantAddInside?.(slot.id);
  };

  const handleAddSlotBefore = () => {
    if (isFirstInBar) onAddSlotBefore(slot.id);
    else onCantAddInside?.(slot.id);
  };

  const handleDeleteSlot = () => {
    onDeleteSlot(slot.id);
  };

  const handleOpenChordEditor = () => {
    setIsChordEditorOpen(true);
  };

  // Handle chord area press (two-tap: activate -> open modal)
  const handleChordAreaPress = useCallback(() => {
    const isCurrentlyActive = activeSlotId === slot.id;

    if (isCurrentlyActive) {
      // Second tap - open chord editor modal
      handleOpenChordEditor();
    } else {
      // First tap - activate this slot
      if (onSlotPress) {
        onSlotPress(slot.id);
      }
    }
  }, [activeSlotId, slot.id, onSlotPress]);

  // Handle lyrics area press (two-tap: activate -> open modal)
  const handleLyricsAreaPress = useCallback(() => {
    const isCurrentlyActive = activeSlotId === slot.id;

    if (isCurrentlyActive) {
      // Second tap - open lyrics modal
      setIsLyricsModalOpen(true);
    } else {
      // First tap - activate this slot
      if (onSlotPress) {
        onSlotPress(slot.id);
      }
    }
  }, [activeSlotId, slot.id, onSlotPress]);

  // Handle empty slot press
  const handleEmptySlotPress = useCallback(() => {
    const isCurrentlyActive = activeSlotId === slot.id;

    if (isCurrentlyActive) {
      // Second tap - open chord editor
      handleOpenChordEditor();
    } else {
      // First tap - activate this slot
      if (onSlotPress) {
        onSlotPress(slot.id);
      }
    }
  }, [activeSlotId, slot.id, onSlotPress]);

  const handleCloseChordEditor = () => {
    setIsChordEditorOpen(false);
  };

  const handleApplyChord = (data: { chord?: ChordObjectModelV3; customText?: string }) => {
    // Handle custom text mode - clear chord
    if (data.customText) {
      onUpdateChord(slot.id, null, data.customText);
    }
    // Handle chord builder mode - clear custom text
    else if (data.chord) {
      onUpdateChord(slot.id, data.chord, undefined);
    }
    // Handle empty slot - clear both
    else {
      onUpdateChord(slot.id, null, undefined);
    }
    handleCloseChordEditor();
  };

  const handleClearChord = () => {
    onUpdateChord(slot.id, null, undefined);
    handleCloseChordEditor();
  };

  // Editor shortcuts. Beats applies the chord edits first (if any), then opens
  // the bar editor, or the time signature editor when the song has no meter.
  const openBeatsTarget = onRequestSetTimeSignature
    ?? (onOpenBeatsEditor ? () => onOpenBeatsEditor(slot.id) : undefined);
  const handleOpenBeatsFromEditor = (edits: { chord?: ChordObjectModelV3; customText?: string } | null) => {
    if (edits) handleApplyChord(edits);
    else handleCloseChordEditor();
    openBeatsTarget?.();
  };
  const beatsEditorLabel = !onRequestSetTimeSignature && meter && barPosition
    ? `Beats · ${formatBeats(barPosition.layout.lengthUnits)}/${meter.numerator}`
    : 'Beats';

  const handleApplyLyrics = (lyrics: string) => {
    if (onUpdateLyrics) {
      onUpdateLyrics(slot.id, lyrics);
    }
  };

  const isSlotActive = activeSlotId === slot.id;

  // Build the display string using getChordDisplayText
  const displayChordString = getChordDisplayText(slot);

  // A slot with no chord and no custom text. With lyrics shown, it still
  // renders as a full slot (silence mark over its lyrics area, so lyrics can be
  // added without a chord); otherwise as an empty one.
  const isChordless = !slot.chord && !slot.customText;
  const hasChordArea = !isChordless || showLyrics;

  // What a slot with no chord shows in its chord area: the silence mark (a
  // crossed-out note; it plays silent), swapped for the add-chord hint while the slot is active,
  // since the next click opens the chord editor.
  const chordlessMark = isSlotActive ? (
    <span style={styles.emptySlotText}>{CONSTANTS.EMPTY_SLOT_PLACEHOLDER}</span>
  ) : (
    <span role="img" aria-label={CONSTANTS.SILENCE_LABEL} data-testid={`silence-mark-${slot.id}`} style={styles.silenceMark}>
      <SilenceIcon size={theme.sizes.icon.xl} color={theme.colors.textSecondary} />
    </span>
  );

  // Beat dots (Beats on, song has a meter)
  const beatLayout = barPosition?.layout ?? null;
  const beatDots = useMemo(
    () => (showBeats && meter && beatLayout ? buildBeatDots(beatLayout, meter) : null),
    [showBeats, meter, beatLayout],
  );
  const beatDotsView = beatDots && beatLayout && (
    <BeatDots dots={beatDots} lengthUnits={beatLayout.lengthUnits} />
  );

  // Calculate degree display
  let degreeDisplay: string | null = null;
  if (showDegrees && selectedKey && slot.chord) {
    degreeDisplay = getChordDegree(slot.chord, selectedKey);
  }

  // Calculate notes string
  let notesString: string | null = null;
  if (slot.chord?.root) {
    const notesArray = getChordNotes(slot.chord);
    notesString = notesArray.join(' - ');
  }

  // Calculate widths when content changes
  useEffect(() => {
    const contentKey = `${displayChordString}|${notesString || ''}|${slot.lyrics || ''}`;

    if (contentKey !== previousContentRef.current) {
      previousContentRef.current = contentKey;

      if (!displayChordString) {
        setSlotWidths({
          chordOnly: theme.sizes.slot.minWidth,
          chordAndNotes: theme.sizes.slot.minWidth,
          chordAndLyrics: theme.sizes.slot.minWidth,
          chordNotesAndLyrics: theme.sizes.slot.minWidth,
        });
        return;
      }

      requestAnimationFrame(() => {
        let chordWidth = 0;
        let notesWidth = 0;
        let lyricsWidth = 0;

        // Measure chord text
        if (measureChordTextRef.current) {
          chordWidth = measureChordTextRef.current.getBoundingClientRect().width;
        }

        // Measure notes text if exists
        if (notesString && measureNotesTextRef.current) {
          notesWidth = measureNotesTextRef.current.getBoundingClientRect().width;
        }

        // Measure lyrics text if exists
        if (slot.lyrics && measureLyricsTextRef.current) {
          lyricsWidth = measureLyricsTextRef.current.getBoundingClientRect().width;
        }

        // Calculate padding
        // slotInner / lyricsInner horizontal padding plus the area margins
        const areaInset = theme.spacing.xxs + theme.spacing.xs;
        const chordPadding = theme.spacing.sm * 2 + areaInset;
        const notesPadding = areaInset;
        const lyricsPadding = theme.spacing.sm * 2 + areaInset;

        const chordTotalWidth = chordWidth + chordPadding;
        const notesTotalWidth = notesWidth + notesPadding;
        const lyricsTotalWidth = lyricsWidth + lyricsPadding;

        // Calculate all 4 width combinations
        setSlotWidths({
          chordOnly: Math.max(theme.sizes.slot.minWidth, chordTotalWidth),
          chordAndNotes: Math.max(theme.sizes.slot.minWidth, chordTotalWidth, notesTotalWidth),
          chordAndLyrics: Math.max(theme.sizes.slot.minWidth, chordTotalWidth, lyricsTotalWidth),
          chordNotesAndLyrics: Math.max(theme.sizes.slot.minWidth, chordTotalWidth, notesTotalWidth, lyricsTotalWidth),
        });
      });
    }
  }, [displayChordString, notesString, slot.lyrics, theme.spacing.sm, theme.spacing.xs]);

  // Determine which width to use based on showNotes and showLyrics
  const getSlotWidth = () => {
    if (showNotes && showLyrics) return slotWidths.chordNotesAndLyrics;
    if (showNotes) return slotWidths.chordAndNotes;
    if (showLyrics) return slotWidths.chordAndLyrics;
    return slotWidths.chordOnly;
  };

  // Up to 8 dots fit the minimum width; longer bars (12/8) widen the slot
  const dotsMinWidth = beatDots
    ? beatDotsWidth(beatDots, theme.spacing.xs) + theme.spacing.sm * 2 + theme.spacing.xxs + theme.spacing.xs
    : 0;
  const slotWidth = Math.max(getSlotWidth(), dotsMinWidth);

  return (
    <>
      {/* Measurement elements */}
      <span
        ref={measureChordTextRef}
        style={{
          ...styles.measurementElement,
          ...styles.chordText,
        }}
      >
        {displayChordString}
      </span>

      {notesString && (
        <span
          ref={measureNotesTextRef}
          style={{
            ...styles.measurementElement,
            ...styles.notesText,
          }}
        >
          {notesString}
        </span>
      )}

      {slot.lyrics && (
        <span
          ref={measureLyricsTextRef}
          style={{
            ...styles.measurementElement,
            ...styles.lyricsText,
          }}
        >
          {slot.lyrics}
        </span>
      )}

      <div
        ref={slotContainerRef}
        style={{
          ...styles.slotContainer,
          width: `${slotWidth}px`,
          minWidth: `${slotWidth}px`,
        }}
      >
        {hasChordArea ? (
          // Slot with a chord, custom text or shown lyrics - areas for chord and lyrics
          <div style={styles.slotWithChordContainer}>
            {/* Chord Area */}
            <SlotArea
              type="chord"
              onPress={handleChordAreaPress}
              isActive={isSlotActive}
              showLyrics={showLyrics}
            >
              {/* A lyric-only slot has no chord: it shows the silence mark (or
                  the add-chord hint) above its lyrics. */}
              <div style={styles.slotInner}>
                {isChordless ? (
                  <>
                    {chordlessMark}
                    {beatDotsView}
                  </>
                ) : (
                  <>
                    {degreeDisplay && (
                      <span style={styles.degreeText}>
                        {degreeDisplay}
                      </span>
                    )}
                    <span style={styles.chordText}>
                      {displayChordString}
                    </span>
                    {beatDotsView}
                    {showNotes && notesString && (
                      <span style={styles.notesText}>
                        {notesString}
                      </span>
                    )}
                  </>
                )}
              </div>

            </SlotArea>

            {/* Lyrics Area - only when showLyrics is true */}
            {showLyrics && (
              <SlotArea
                type="lyrics"
                onPress={handleLyricsAreaPress}
                isActive={isSlotActive}
                >
                <div style={styles.lyricsInner}>
                  <span
                    style={{
                      ...styles.lyricsText,
                      ...(slot.lyrics ? {} : styles.lyricsPlaceholder),
                    }}
                  >
                    {slot.lyrics || CONSTANTS.LYRICS_PLACEHOLDER}
                  </span>
                </div>
              </SlotArea>
            )}
          </div>
        ) : (
          // Empty slot
          <SlotArea
            type="empty"
            onPress={handleEmptySlotPress}
            isActive={isSlotActive}
          >
            <div style={styles.emptySlotContainer}>
              {chordlessMark}
              {beatDotsView}
            </div>

          </SlotArea>
        )}
        {!isLastInBar ? (
          <div style={styles.beatDivider} data-testid={`beat-divider-${slot.id}`} />
        ) : (
          !isLast && <div style={styles.slotSeparator} data-testid={`bar-line-${slot.id}`} />
        )}
      </div>

      {/* The active slot's controls float above it (mirrors RN's SlotActionPill) */}
      {isSlotActive && (
        <SlotActionPill
          anchorRef={slotContainerRef}
          canDelete={canDelete}
          canSetBeats={showBeats && meter !== null}
          addBeforeDisabled={!isFirstInBar}
          addAfterDisabled={!isLastInBar}
          onEdit={handleOpenChordEditor}
          onSetBeats={() => onOpenBeatsEditor?.(slot.id)}
          onAddBefore={handleAddSlotBefore}
          onAddAfter={handleAddSlot}
          onDelete={handleDeleteSlot}
        />
      )}

      <ChordEditorModal
        isOpen={isChordEditorOpen}
        onClose={handleCloseChordEditor}
        onApply={handleApplyChord}
        onClear={handleClearChord}
        onMakeRest={handleClearChord}
        onOpenBeats={openBeatsTarget ? handleOpenBeatsFromEditor : undefined}
        beatsLabel={beatsEditorLabel}
        initialChordData={{ chord: slot.chord || undefined, customText: slot.customText }}
      />

      <LyricsInputModal
        visible={isLyricsModalOpen}
        currentLyrics={slot.lyrics}
        currentChord={displayChordString}
        onClose={() => setIsLyricsModalOpen(false)}
        onAccept={handleApplyLyrics}
      />
    </>
  );
};

export default Slot;
